import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import colors from '../../styles/colors';
import type { User } from '../../types';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 80px;
  box-sizing: border-box;
  z-index: 999;
`;

/* Header è 70px: top modale ≈ metà altezza avatar (35px) */
const MODAL_TOP_OFFSET = 35;

const ModalContent = styled.div`
  z-index: 9999;
  width: 100%;
  max-width: 320px;
  margin: 0 16px;

  & .modal-dialog {
    position: fixed !important;
    top: ${MODAL_TOP_OFFSET}px !important;
    left: 8px !important;
    margin: 0 !important;
    max-width: 320px;
    width: calc(100% - 16px);
  }

  & .modal-content {
    background-color: ${colors.bgColorBlack2};
    border: 2px solid ${colors.bgColorBordeaux};
    border-radius: 8px;
    color: white;
  }

  & .modal-header {
    border-bottom-color: ${colors.bgColorBordeaux};
    background-color: ${colors.bgColorBlack2};
    color: white;
  }

  & .modal-title {
    color: white;
  }

  & .btn-close {
    filter: invert(1);
  }

  & .modal-body {
    background-color: ${colors.bgColorBlack2};
    color: white;
  }

  & .modal-footer {
    border-top-color: ${colors.bgColorBordeaux};
    background-color: ${colors.bgColorBlack2};
  }

  & .section_menu_name {
    font-weight: 600;
    font-size: 18px;
    padding: 7px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    cursor: pointer;
    color: white;
    transition: background-color 0.2s ease, transform 0.1s ease;
    -webkit-tap-highlight-color: rgba(139, 0, 0, 0.3);
    user-select: none;
    -webkit-user-select: none;
  }

  /* Hover solo per dispositivi non-touch */
  @media (hover: hover) {
    & .section_menu_name:hover {
      background-color: rgba(139, 0, 0, 0.3);
    }
  }

  /* Active per tutti i dispositivi (touch e non) */
  & .section_menu_name:active {
    background-color: rgba(139, 0, 0, 0.5);
    transform: scale(0.98);
  }

  & .section_menu_name p {
    margin: 0;
    color: white;
  }
`;

const MessaggiRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;

  & .messaggi-unread-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: gold;
    flex-shrink: 0;
  }
`;

const UserOverview = styled.div`
  padding: 12px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);

  & .user-overview-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 6px;
  }

  & .user-overview-name {
    font-weight: 600;
    font-size: 15px;
    color: white;
    margin: 0 0 4px 0;
    word-break: break-word;
  }

  & .user-overview-email {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
    word-break: break-all;
  }
`;

interface MenuUserMobileProps {
  user: User;
  handleVisiblityMenuMobile: () => void;
  handleLogOutClick: () => void;
  unreadNotificationsCount?: number;
  onNotificationsRead?: () => void;
}

const MenuUserMobile: FC<MenuUserMobileProps> = ({
  user,
  handleVisiblityMenuMobile,
  handleLogOutClick,
  unreadNotificationsCount = 0,
  onNotificationsRead,
}) => {
  const navigate = useNavigate();

  const handleNavigationClick = (link: string): void => {
    if (onNotificationsRead && link === 'messaggi') {
      onNotificationsRead();
    }
    handleVisiblityMenuMobile();
    navigate(link);
  };

  return (
    <Container>
      <ModalContent className="modal d-block">
        <div className="modal-dialog">
          <div className="modal-content">
            <form>
              <div className="modal-header">
                <h5 className="modal-title">Pannello</h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => handleVisiblityMenuMobile()}
                />
              </div>
              <div className="modal-body">
                <UserOverview>
                  <div className="user-overview-label">Il tuo account</div>
                  <p className="user-overview-name">{user.name || user.username}</p>
                  <p className="user-overview-email">{user.email}</p>
                </UserOverview>

                {(!!user.is_founder || !!user.is_admin || !!user.is_super_admin) && (
                  <div
                    className="section_menu_name"
                    onClick={() => handleNavigationClick('/admin/catalog/mobile')}
                  >
                    <p>Catalogo Carte</p>
                  </div>
                )}

                <div
                  className="section_menu_name"
                  onClick={() => handleNavigationClick('list-users')}
                >
                  <p>Lista Utenti</p>
                </div>

                <div
                  className="section_menu_name"
                  onClick={() => handleNavigationClick('messaggi')}
                >
                  <MessaggiRow>
                    <p>Messaggi</p>
                    {unreadNotificationsCount > 0 && (
                      <span
                        className="messaggi-unread-dot"
                        title={`${unreadNotificationsCount} non lette`}
                      />
                    )}
                  </MessaggiRow>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  data-bs-dismiss="modal"
                  onClick={() => handleLogOutClick()}
                >
                  Logout
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                  onClick={() => handleVisiblityMenuMobile()}
                >
                  Chiudi
                </button>
              </div>
            </form>
          </div>
        </div>
      </ModalContent>
    </Container>
  );
};

export default MenuUserMobile;
